
import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Plus, Edit, Trash2 } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useModulesForDropdown } from '@/hooks/useModulesForDropdown'
import { useSchools } from '@/hooks/useSchools'

const AdminTasks = () => {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingTask, setEditingTask] = useState(null)
  const [activeTab, setActiveTab] = useState('global')
  const { toast } = useToast()
  const { modules: allModules, loading: modulesLoading } = useModulesForDropdown()
  const { schools, loading: schoolsLoading } = useSchools()

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    module: '',
    domain: '',
    type: 'discover',
    due_date: '',
    school_id: ''
  })

  const domains = [
    { value: 'self', label: 'Self' },
    { value: 'wealth', label: 'Wealth' },
    { value: 'social', label: 'Social' }
  ]

  // Filter modules by selected domain using actual module categories
  const availableModules = allModules.filter(module => {
    if (!formData.domain) return true // Show all if no domain selected
    
    // Map domain selection to actual module categories
    if (formData.domain === 'wealth') {
      return module.category === 'Wealth'
    } else if (formData.domain === 'self') {
      return module.category === 'Self'
    } else if (formData.domain === 'social') {
      return module.category === 'Social'
    }
    return false
  })

  // Debug logging
  console.log('AdminTasks - Module filtering:', {
    selectedDomain: formData.domain,
    allModulesCount: allModules.length,
    availableModulesCount: availableModules.length,
    availableModules: availableModules.map(m => ({ label: m.label, category: m.category }))
  })

  useEffect(() => {
    fetchTasks()

    // Set up real-time subscription for tasks
    const tasksChannel = supabase
      .channel('admin-tasks-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_tasks'
        },
        () => {
          fetchTasks()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(tasksChannel)
    }
  }, [])

  const fetchTasks = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_tasks')
        .select(`
          *,
          schools (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error
      setTasks(data || [])
    } catch (error: any) {
      toast({
        title: "Error fetching tasks",
        description: error.message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // Filter tasks based on active tab
  const filteredTasks = tasks.filter((task: any) => {
    if (activeTab === 'global') {
      return !task.school_id
    } else {
      return task.school_id
    }
  })

  // Group school-specific tasks by school
  const tasksBySchool = filteredTasks.reduce((acc: any, task: any) => {
    if (task.school_id) {
      const schoolName = task.schools?.name || 'Unknown School'
      if (!acc[schoolName]) {
        acc[schoolName] = []
      }
      acc[schoolName].push(task)
    }
    return acc
  }, {})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.title.trim()) return

    try {
      const taskData = {
        ...formData,
        due_date: formData.due_date || null,
        school_id: formData.school_id === 'global' ? null : formData.school_id || null
      }

      if (editingTask) {
        const { error } = await supabase
          .from('admin_tasks')
          .update(taskData)
          .eq('id', editingTask.id)
        
        if (error) throw error
        toast({ title: "Task updated successfully" })
      } else {
        const { error } = await supabase
          .from('admin_tasks')
          .insert(taskData)
        
        if (error) throw error
        toast({ title: "Task created successfully" })
      }

      setDialogOpen(false)
      setEditingTask(null)
      setFormData({
        title: '',
        description: '',
        module: '',
        domain: '',
        type: 'discover',
        due_date: '',
        school_id: ''
      })
      fetchTasks()
    } catch (error: any) {
      toast({
        title: "Error saving task",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleEdit = (task: any) => {
    setEditingTask(task)
    setFormData({
      title: task.title,
      description: task.description || '',
      module: task.module || '',
      domain: task.domain || '',
      type: task.type || 'discover',
      due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : '',
      school_id: task.school_id || 'global'
    })
    setDialogOpen(true)
  }

  const handleDelete = async (taskId: string) => {
    try {
      const { error } = await supabase
        .from('admin_tasks')
        .delete()
        .eq('id', taskId)

      if (error) throw error
      toast({ title: "Task deleted successfully" })
      fetchTasks()
    } catch (error: any) {
      toast({
        title: "Error deleting task",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Task Management</CardTitle>
          <CardDescription>Loading tasks...</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Task Management</CardTitle>
            <CardDescription>Create and manage admin tasks. Set type as 'discover' for general tasks or 'inspiration' for quiz mappings.</CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setEditingTask(null)
                setFormData({
                  title: '',
                  description: '',
                  module: '',
                  domain: '',
                  type: 'discover',
                  due_date: '',
                  school_id: ''
                })
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Task
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>{editingTask ? 'Edit Task' : 'Create New Task'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Type</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="discover">Discover</SelectItem>
                      <SelectItem value="inspiration">Inspiration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Select value={formData.domain} onValueChange={(value) => setFormData({ ...formData, domain: value, module: '' })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select domain" />
                    </SelectTrigger>
                    <SelectContent>
                      {domains.map(domain => (
                        <SelectItem key={domain.value} value={domain.value}>
                          {domain.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="module">Module</Label>
                  <Select value={formData.module} onValueChange={(value) => setFormData({ ...formData, module: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder={modulesLoading ? "Loading..." : "Select module"} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableModules.map(module => (
                        <SelectItem key={module.value} value={module.value}>
                          {module.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="school_id">Target University</Label>
                  <Select value={formData.school_id} onValueChange={(value) => setFormData({ ...formData, school_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select target university (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="global">Global (All Universities)</SelectItem>
                      {schools.map(school => (
                        <SelectItem key={school.id} value={school.id}>{school.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Input
                    id="due_date"
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  />
                </div>
                <Button type="submit" className="w-full">
                  {editingTask ? 'Update Task' : 'Create Task'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="global">Global Tasks</TabsTrigger>
            <TabsTrigger value="school-specific">School-Specific Tasks</TabsTrigger>
          </TabsList>
          
          <TabsContent value="global" className="mt-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Module</TableHead>
              <TableHead>Domain</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
                {filteredTasks.map((task: any) => (
              <TableRow key={task.id}>
                <TableCell className="font-medium">{task.title}</TableCell>
                <TableCell>
                  <Badge variant={task.type === 'discover' ? 'default' : 'secondary'}>
                    {task.type}
                  </Badge>
                </TableCell>
                <TableCell>{task.module}</TableCell>
                <TableCell>{task.domain}</TableCell>
                <TableCell>
                  {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(task)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(task.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
                {filteredTasks.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No global tasks created yet. Click "Add Task" to create your first task.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
          </TabsContent>

          <TabsContent value="school-specific" className="mt-4">
            {Object.keys(tasksBySchool).length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No school-specific tasks created yet. Click "Add Task" and select a university to create school-specific tasks.
              </div>
            ) : (
              Object.entries(tasksBySchool).map(([schoolName, schoolTasks]: [string, any]) => (
                <div key={schoolName} className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">{schoolName}</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Module</TableHead>
                        <TableHead>Domain</TableHead>
                        <TableHead>Due Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {schoolTasks.map((task: any) => (
                        <TableRow key={task.id}>
                          <TableCell className="font-medium">{task.title}</TableCell>
                          <TableCell>
                            <Badge variant={task.type === 'discover' ? 'default' : 'secondary'}>
                              {task.type}
                            </Badge>
                          </TableCell>
                          <TableCell>{task.module}</TableCell>
                          <TableCell>{task.domain}</TableCell>
                          <TableCell>
                            {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(task)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(task.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}

export default AdminTasks
